import type { GetServerSideProps } from 'next';
import type { PageProps } from 'lib/queries/pages';
import type { Page } from 'types/page';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import WebPageJson from 'components/structured-data/webpage';
import ProductJson from 'components/structured-data/product';
import PageWrap from 'components/page-wrap';
import Blocks from 'components/blocks';

import { getPageServerSide } from 'lib/queries/pages';
import { parse } from 'cookie';
import { POSTHOG_COOKIE_KEY } from 'lib/cookies/posthog-cookie';
import { default as posthogAPI } from 'lib/posthog';
import camelcaseKeys from 'camelcase-keys';
import { usePostHog } from 'posthog-js/react';
import StickyMobileCta from 'components/sticky-mobile-cta';
import MedicalReviewer from 'components/medical-reviewer';
import MedicalWebPageJson from 'components/structured-data/medical-webpage';
import ServiceJson from 'components/structured-data/service';

const HeroCard = dynamic(() => import('components/hero-card'));
const HeroCardNew = dynamic(() => import('components/hero-card-new'));
const Loader = dynamic(() => import('components/loader'));

const staticPage = [
  {
    id: 82234,
    date: '2023-06-26T02:19:26',
    modified: '2026-09-24T08:23:00',
    slug: 'oklahoma',
    title: 'Get an Oklahoma (OK) Medical Marijuana Card Online',
    content: '',
    acf: {
      city: '',
      page_type: 'card',
      has_referral: true,
      hero: {
        title: 'Get an Oklahoma Medical Card',
        rich_title: '',
        renew_title: '',
        sub_title: '',
        form_id: '',
        form_type: 'partner',
        form_title: '',
        form_label: '',
        renew_form_content_items: false,
        form_submit_text: 'Get Started',
        form_submit_url: '',
        address: '',
        description:
          '<p>See an OK-licensed medical marijuana doctor online in minutes and get the cannabis care you deserve.</p>\n',
        renew_description: '',
        image: null,
        image_alignment: 'right',
        steps: false,
        button: null,
        download_button: null,
        badges: [
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
          {
            label: '',
            icon: null,
          },
        ],
        google_my_business_details: {
          medical_card_utm: '',
          cannabis_counseling_services_utm: '',
          phone_number: '',
        },
        webinar_members: false,
        quick_links: [
          {
            section: 'qualifying-conditions',
            title: 'Do I qualify?',
            description: 'Check the qualifying conditions within seconds.',
            icon: {
              id: 95920,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-qualify.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'cost-information',
            title: 'Affordable price',
            description: 'Risk-free process. Get approved or your money back.',
            icon: {
              id: 95921,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-affordable.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
          {
            section: 'quick-steps',
            title: 'Quick, easy process',
            description:
              'Book an appointment, consult with a doctor, and receive your certification in just a few minutes.',
            icon: {
              id: 95922,
              src: 'https://admin.leafwell.com/app/uploads/2025/11/icon-quick-easy.svg',
              width: 1,
              height: 1,
              srcSet: false,
              alt: '',
            },
          },
        ],
        latitude: '',
        longitude: '',
        opening_hours: false,
      },
      linktree: {
        categories: false,
      },
      redirect_medical_card: true,
      redirect_product_endpoint: '',
      custom_state_code: '',
      pause_appointments: false,
      is_affiliate: false,
      affiliate_logo: null,
      affiliate_website: '',
      form_url: '',
      vpd_page: [],
      is_experiment: false,
      is_renewal: false,
      is_clinic: false,
      total_medical_card_price: '59',
      name: 'Get an Oklahoma (OK) Medical Marijuana Card Online',
      description:
        'Get your OK medical marijuana card online for just $59. Connect with a Leafwell healthcare provider now and get certified fast!',
      image_object_caption: 'Oklahoma (OK) Medical Marijuana Card by Leafwell',
      product: {
        name: 'Oklahoma Medical Marijuana Card',
        description:
          'An Oklahoma medical marijuana card from Leafwell allows you to purchase cannabis legally from dispensaries throughout OK.',
        image_url: {
          id: 74486,
          src: 'https://admin.leafwell.com/app/uploads/2024/01/OK.jpg',
          width: 420,
          height: 428,
          srcSet:
            'https://admin.leafwell.com/app/uploads/2024/01/OK.jpg 420w, https://admin.leafwell.com/app/uploads/2024/01/OK-393x400.jpg 393w',
          alt: 'Ok card',
        },
      },
      state_product_image: null,
      is_vpd_landing: false,
      vpd_logo: null,
      price: {
        price: '',
        new_price: '',
        percentage_discount: '',
        discount_code: '',
        free_discount_code: '',
      },
      patient_renewal_dropdown: false,
      patient_renewal_dropdown_info:
        'Prior to your consultation, we will verify your status within the program',
      affiliate_page: [],
      webinar_info: {
        webinar_title: '',
        webinar_description: '',
        webinar_zoom_url: '',
      },
      vpd_partner_name: false,
      states: ['OK'],
      blocks: [
        {
          acf_fc_layout: 'rich_text',
          instance_id: '8895dee9',
          container_size: 'full',
          content:
            '<section class="px-4 lg:px-10 grid gap-y-6 lg:gap-y-12 mx-auto py-6 md:py-16" style="max-width: 1536px; justify-items: center; color: #16131f; line-height: 1.7; box-sizing: border-box;">\n<p style="font-size: 1.08rem; line-height: 1.62; margin: 0 0 22px; color: #221e2e;"><strong style="color: #6b4fbb;">Oklahoma medical marijuana card, in short:</strong> You can get certified online. Oklahoma requires no qualifying condition — a licensed physician decides. A Leafwell telehealth evaluation is $59, plus OMMA&#8217;s non-refundable state fee of $104.30, or $22.50 if you qualify for the reduced rate. OMMA approves within 14 business days; licenses last up to two years.</p>\n<div style="display: flex; flex-wrap: wrap; gap: 14px;">\n<div style="flex: 1 1 240px; background: #F7F6FA; border-radius: 12px; padding: 15px 17px; box-sizing: border-box;"><span style="display: block; font-size: .9rem; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: #35a96a; margin-bottom: 3px;">Who qualifies</span><span style="display: block; font-size: 0.95rem; color: #16131f; line-height: 1.45;">Any <strong>Oklahoma resident aged 18+</strong> with proof of identity and residency. No qualifying-condition list — a state-licensed physician decides based on professional judgment. Under-18 applicants apply through a parent or legal guardian and need two physicians&#8217; recommendations.</span></div>\n<div style="flex: 1 1 240px; background: #F7F6FA; border-radius: 12px; padding: 15px 17px; box-sizing: border-box;"><span style="display: block; font-size: .9rem; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: #35a96a; margin-bottom: 3px;">Cost</span><span style="display: block; font-size: .95rem; color: #16131f; line-height: 1.45;"><strong>$59</strong> Leafwell telehealth evaluation (new patients and renewals) + OMMA state fee of $100 + $4.30 processing = $163.30. Reduced state fee $20 + $2.50 for Medicaid (SoonerSelect), Medicare or 100% disabled-veteran applicants = $81.50 total. OMMA&#8217;s fees are non-refundable.</span></div>\n<div style="flex: 1 1 240px; background: #F7F6FA; border-radius: 12px; padding: 15px 17px; box-sizing: border-box;"><span style="display: block; font-size: .9rem; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: #35a96a; margin-bottom: 3px;">How long it lasts</span><span style="display: block; font-size: .95rem; color: #16131f; line-height: 1.45;"><strong>Up to two years.</strong> OMMA has 14 business days to review an application, plus 1–2 days to print the card. No grace period once it expires — renew early.</span></div>\n<div style="flex: 1 1 240px; background: #F7F6FA; border-radius: 12px; padding: 15px 17px; box-sizing: border-box;"><span style="display: block; font-size: .9rem; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: #35a96a; margin-bottom: 3px;">Online or in person</span><span style="display: block; font-size: .95rem; color: #16131f; line-height: 1.45;"><strong>Fully online.</strong> Telehealth evaluation statewide; the application is filed through the <strong>OMMA MedPortal</strong> at oklahoma.gov. No in-person clinic visit required at any stage.</span></div>\n</div>\n</section>\n',
          section_id: '',
        },
        {
          acf_fc_layout: 'benefits_of_mmc',
          instance_id: 'ac3c3285',
          section_id: 'key-benefits-of-mmc',
          title:
            'The <span class="text-purple-500">key benefits</span> of a medical marijuana card in Oklahoma',
          description: '',
          access_to_dispensaries: {
            highlight: true,
            title:
              '<span class="text-purple-500">Access</span> to dispensaries',
            description:
              '<p>Having a medical card is the only way to legally purchase, use, and possess cannabis in the state of Oklahoma.</p>\n<p>Recreational use is illegal.</p>\n',
          },
          tax_savings: {
            highlight: false,
            title: 'Tax savings',
            description: '',
          },
          cultivation: {
            highlight: false,
            title: 'Cultivation',
            description:
              '<p>Medical card holders can <strong>grow up to 12 plants</strong>, while non-card holders are not allowed to grow cannabis.</p>\n',
          },
          higher_possession: {
            highlight: false,
            title: '<span class="text-purple-500">Higher</span> possession',
            description:
              '<p>Medical card holders can <strong>possess 3 oz of cannabis products</strong>.</p>\n<p>Non-card holders cannot legally possess cannabis products.</p>\n',
          },
          access_to_products: {
            highlight: false,
            title: 'Access <strong>higher-potency</strong> cannabis products',
            description: '',
          },
          lower_age_restriction: {
            highlight: false,
            title:
              'Get <span class="text-purple-500">legal cannabis</span> at age 18+, rather than 21+',
            description:
              '<p>Patients 18+ can qualify for a medical card.</p>\n<p>Minors can also get a card: a parent or legal guardian applies with them and two physicians must sign the recommendation.</p>\n',
          },
          legal_protection: {
            highlight: false,
            title:
              'Enjoy <span class="text-purple-500">legal protections</span> for employment',
            description:
              '<p>Employers may not discipline, fire or refuse to hire you solely for holding a card or testing positive for marijuana, unless your job is safety-sensitive or you use or possess marijuana at work. From November 1, 2026 (HB 3127), employers with a compliant written drug-testing policy may act on a positive test.</p>\n<p>Landlords cannot refuse to lease to you or penalize you solely for being a licensed patient, unless doing so would cost them a federal benefit.</p>\n',
          },
        },
        {
          acf_fc_layout: 'qualifying_conditions',
          section_id: 'how-it-works',
          instance_id: '468a38ee',
          title:
            'Oklahoma\'s Medical Marijuana Program: <span class="text-purple-500">How It Works</span>',
          intro: '',
          conditions_list:
            '<p>Oklahoma runs one of the most accessible medical marijuana programs in the country. Unlike most states, Oklahoma does not require a specific qualifying condition — a state-licensed physician recommends medical marijuana based on their professional judgment. To get an Oklahoma medical marijuana card, you complete a telehealth evaluation with a licensed physician, then apply to the Oklahoma Medical Marijuana Authority (OMMA). OMMA reviews applications within 14 business days, and since January 6, 2026 you can shop at any licensed Oklahoma dispensary with your OMMA approval email and a photo ID while your card is in the mail. Below, we break down the cost, the steps, and everything you need to apply.</p>\n',
          button: '',
        },
        {
          acf_fc_layout: 'reviews',
          instance_id: 'ef53ac31',
          section_id: 'testimonials',
          title: 'Trusted by 1 Million Monthly Users',
          intro: '',
          trustpilot: true,
          show_trust_signals: true,
          variant: 'full',
          quoted: false,
        },
        {
          acf_fc_layout: 'cards_image',
          instance_id: '3e14d95f',
          section_id: 'quick-steps',
          title: '4 easy steps to get your OK medical card',
          label: 'Get approved in minutes using Leafwell’s online platform.',
          label_position: 'below_title',
          list_numbered: true,
          cards_background_color: 'beige',
          cards: [
            {
              title: 'Sign up',
              description:
                '<p>Register with Leafwell on any device and consult with a licensed medical marijuana doctor in minutes.</p>\n',
              image: {
                id: 96266,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-signup-yellow.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'sign up icon',
              },
            },
            {
              title: 'Get your certification',
              description:
                '<p>Once approved, your doctor will upload your Physician Recommendation Form to Leafwell’s platform.</p>\n',
              image: {
                id: 96267,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-certificate-purple.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'certificate',
              },
            },
            {
              title: 'Submit your certification',
              description:
                '<p>Log into the OMMA MedPortal to submit your application and pay the state fee: $100 + $4.30 processing, or $20 + $2.50 with proof of Medicaid (SoonerSelect), Medicare or 100% disabled-veteran status.</p>\n',
              image: {
                id: 96268,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-upload-cert-green.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: '',
              },
            },
            {
              title: 'Get your card',
              description:
                '<p>Receive an approval email and a patient license identification card in the mail within 14 days.</p>\n',
              image: {
                id: 96269,
                src: 'https://admin.leafwell.com/app/uploads/2025/12/icon-get-card-pink.svg',
                width: 1,
                height: 1,
                srcSet: false,
                alt: 'card',
              },
            },
          ],
          description: '',
          button: '',
        },
        {
          acf_fc_layout: 'youtube_video',
          instance_id: '9d042831',
          section_id: '',
          title: 'A few more questions about the process?',
          content:
            'Watch this video to learn more about getting a medical marijuana card in Oklahoma',
          variant: 'none',
          video_size: 'small',
          youtube_videos: [
            {
              youtube_video_id: 'nknyIOw5S54',
              image: null,
              play_button_style: 'secondary',
            },
          ],
        },
        {
          acf_fc_layout: 'table',
          instance_id: 'a5dd96ae',
          section_id: 'cost-information',
          display_product_state_image: false,
          title: 'How much does an Oklahoma medical marijuana card cost?',
          title_tag: 'h3',
          intro_text:
            '<p>Oklahoma medical marijuana patients save more with Leafwell. Get your money back if not approved.</p>\n',
          table: {
            columns: [
              {
                column_type: 'values',
                label: '',
                values: [
                  {
                    value_text: 'Medical marijuana certificate',
                    value_check: false,
                  },
                  {
                    value_text: 'State application fee',
                    value_check: false,
                  },
                  {
                    value_text: 'Total',
                    value_check: false,
                  },
                ],
              },
              {
                column_type: 'values',
                label: 'New with Leafwell',
                values: [
                  {
                    value_text: '$59',
                    value_check: false,
                  },
                  {
                    value_text: '$20-$100',
                    value_check: false,
                  },
                  {
                    value_text: '$79-$159',
                    value_check: false,
                  },
                ],
              },
              {
                column_type: 'values',
                label: 'Renewal with Leafwell',
                values: [
                  {
                    value_text: '$59',
                    value_check: false,
                  },
                  {
                    value_text: '$20-$100',
                    value_check: false,
                  },
                  {
                    value_text: '$79-$159',
                    value_check: false,
                  },
                ],
              },
            ],
          },
          button: {
            title: 'Apply Now',
            url: '#modal-mc-signup',
            target: '',
          },
        },
        {
          acf_fc_layout: 'content_and_banner',
          instance_id: '29a90db7',
          section_id: 'qualifying-conditions',
          title: 'How to Qualify for a Medical Marijuana Card in Oklahoma',
          background_color: 'beige',
          content_left:
            '<p>To apply for a medical marijuana card in Oklahoma, you must be a legal resident of Oklahoma and provide proof of residency. Patients must be 18 to apply for a medical marijuana card for themselves. Oklahoma law does not include any qualifying conditions. This makes it one of the most lenient medical marijuana programs in the United States and may account for why roughly one in 13 Oklahomans holds a patient license (319,965 active licenses in OMMA’s January 2026 report).</p>\n',
          content_right:
            '<p>Instead, each medical marijuana doctor can issue recommendations based on their judgment—much like they do when prescribing traditional medications.</p>\n',
          banner_title: 'Convenient, Quality Care<br/> When You Need It Most',
          banner_label:
            "With Leafwell, you'll be matched up with highly knowledgeable, empathetic, licensed medical marijuana doctors or providers.",
          banner_image: {
            id: 76039,
            src: 'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png',
            width: 1568,
            height: 750,
            srcSet:
              'https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-700x335.png 700w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1024x490.png 1024w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-400x191.png 400w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-768x367.png 768w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors-1536x735.png 1536w, https://admin.leafwell.com/app/uploads/2024/03/medical-cannabis-doctors.png 1568w',
            alt: 'health care providers',
          },
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '972d7b4b',
          container_size: 'large',
          content:
            '<h2>2 documents needed to apply for an Oklahoma medical marijuana card</h2>\n<p>You&#8217;ll need to provide the following to apply for a medical marijuana card in Oklahoma:</p>\n<ol>\n<li>Medical Marijuana Recommendation<br />\nYou will receive your Physician Recommendation Form once approved by a Leafwell provider online.</li>\n<li>Proof of Identity<br />\nOK Driver’s License, OK State ID, OK Tribal ID or US Passport or other photo identification issued by the U.S government (e.g. Military ID) with Proof of Residency</li>\n</ol>\n<div data-highlight="button" data-label="Get Your Card" data-ref="#leafwell-signup-form" data-target="_self"></div>\n',
          section_id: '',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '109bb402',
          container_size: 'default',
          content:
            '<h2>Next steps after renewal</h2>\n<p>After downloading your Leafwell doctor’s recommendation, you will need to complete your renewal with the state within 30 days.</p>\n<p>Here&#8217;s a guide on what to do next.</p>\n<h4>Renewing Your Oklahoma Medical Marijuana Card</h4>\n<ol>\n<li>Log in to the <a href="https://medportal.omma.ok.gov/">OMMA MedPortal</a>.\n<ol>\n<li>If verified, you’ll go to your dashboard.</li>\n<li>If not, you’ll be sent to the OMMA Licensing Portal Claim page.\n<ol>\n<li>Select the option that applies to you (existing license/application or new user).</li>\n<li>Enter your DOB and OMMA License Number or Application ID if prompted.</li>\n<li>Once verified, select the Patient Portal.</li>\n</ol>\n</li>\n</ol>\n</li>\n<li>Click the &#8220;Applications&#8221; tab.</li>\n<li>Click &#8220;Apply Now.&#8221;\n<ol>\n<li>Answer the eligibility questions and select the license type you’re applying for.</li>\n<li>Enter your personal information.</li>\n<li>Select your &#8220;Identity Document Type&#8221; and enter the requested information.</li>\n<li>Upload the front and back of your Proof of Identity.</li>\n<li>Upload a digital photograph for your patient ID card. Adjust photo as needed.</li>\n<li>Upload Proof of Oklahoma Residency (if required).</li>\n<li>Enter optional medical condition info.</li>\n<li>Add caregiver information if applicable.</li>\n<li>Enter the information printed on your Leafwell Physician Recommendation Form.\n<ol>\n<li>Please make sure the signature date is no more than 30 days from when you submit your application.</li>\n<li>Upload your Leafwell Physician Recommendation Form.</li>\n</ol>\n</li>\n<li>If you are enrolled in Medicaid, Medicare, or have 100% Disabled Veterans status select &#8220;Yes&#8221; and upload supporting documentation.</li>\n<li>Complete Attestations section.</li>\n<li>Review application. You can download your application before clicking &#8220;Next.&#8221;</li>\n</ol>\n</li>\n<li>Make Payment. On the Payment page, you can review your fee breakdown and payment options.\n<ol>\n<li>Enter Payment Information.</li>\n<li>Fill in your card information, then click &#8220;Next.&#8221;</li>\n<li>Confirm your payment information is correct and click &#8220;Submit Payment.&#8221;</li>\n</ol>\n</li>\n<li>The screen will display a confirmation when you have completed the application submission. You can visit the dashboard and click the &#8220;Applications&#8221; tab to check your status. You do NOT need to mail your application because it was submitted electronically. You will receive a submission confirmation and payment receipt via email.</li>\n<li>Once the application has been submitted, it will be reviewed by the OMMA.\n<ol>\n<li>If approved, an approval letter with your license will be mailed via US Mail within 14 business days of the application submission date.</li>\n<li>If an application is incomplete, you will receive an email stating the reason(s). Log back into your account to make corrections.</li>\n</ol>\n</li>\n<li>Purchase medical marijuana products from a licensed dispensary.\n<ol>\n<li>Present your medical marijuana identification card — or, since January 6, 2026, your OMMA approval email or approved MedPortal status — together with a government photo ID any time you purchase medical marijuana at a dispensary or have marijuana taken to a processor to be concentrated.</li>\n</ol>\n</li>\n</ol>\n<p><em>The OMMA recommends using Chrome, Edge or Firefox as your browser. Please DO NOT use Internet Explorer.</em></p>\n<p><strong>Please note:</strong> You have <strong>30 days</strong> from the date of your certification to complete the state application. Failure to do so may result in additional fees.</p>\n',
          section_id: 'state-steps',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '17d7df3f',
          container_size: 'default',
          content:
            '<h2>How to renew your OK medical marijuana card</h2>\n<p>It’s quick and easy to renew your Oklahoma medical marijuana card with Leafwell. Here’s what you’ll need to do:</p>\n<ol>\n<li>Sign in to your Leafwell account or register as a renewal patient.</li>\n<li>Speak to an Oklahoma-licensed physician online.</li>\n<li>Get approved and download your Physician Recommendation Form.</li>\n<li>Log into your <a href="https://medportal.omma.ok.gov/">OMMA</a> account to submit your renewal application and application fee within 30 days.*</li>\n<li>After your application is submitted, you’ll receive an approval letter and a new license in the mail within 14 business days. Once your renewal is approved, your OMMA approval email and a photo ID let you keep shopping while the new card is in the mail.</li>\n</ol>\n<p>Per state regulations, you must complete the state renewal application within 30 days of receiving your Leafwell recommendation. Failure to do so may result in additional fees.</p>\n',
          section_id: 'renew',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '5659ceb2',
          container_size: 'default',
          content:
            '<h2>Next steps after renewal</h2>\n<p>After downloading your Leafwell doctor’s recommendation, you will need to complete your renewal with the state within 30 days.</p>\n<p>Here&#8217;s a guide on what to do next.</p>\n<h4>Renewing Your Oklahoma Medical Marijuana Card</h4>\n<ol>\n<li>Log in to the <a href="https://medportal.omma.ok.gov/">OMMA MedPortal</a>.\n<ol>\n<li>If verified, you’ll go to your dashboard.</li>\n<li>If not, you’ll be sent to the OMMA Licensing Portal Claim page.\n<ol>\n<li>Select the option that applies to you (existing license/application or new user).</li>\n<li>Enter your DOB and OMMA License Number or Application ID if prompted.</li>\n<li>Once verified, select the Patient Portal.</li>\n</ol>\n</li>\n</ol>\n</li>\n<li>Click the &#8220;Applications&#8221; tab.</li>\n<li>Click &#8220;Apply Now.&#8221;\n<ol>\n<li>Answer the eligibility questions and select the license type you’re applying for.</li>\n<li>Enter your personal information.</li>\n<li>Select your &#8220;Identity Document Type&#8221; and enter the requested information.</li>\n<li>Upload the front and back of your Proof of Identity.</li>\n<li>Upload a digital photograph for your patient ID card. Adjust photo as needed.</li>\n<li>Upload Proof of Oklahoma Residency (if required).</li>\n<li>Enter optional medical condition info.</li>\n<li>Add caregiver information if applicable.</li>\n<li>Enter the information printed on your Leafwell Physician Recommendation Form.\n<ol>\n<li>Please make sure the signature date is no more than 30 days from when you submit your application.</li>\n<li>Upload your Leafwell Physician Recommendation Form.</li>\n</ol>\n</li>\n<li>If you are enrolled in Medicaid, Medicare, or have 100% Disabled Veterans status select &#8220;Yes&#8221; and upload supporting documentation.</li>\n<li>Complete Attestations section.</li>\n<li>Review application. You can download your application before clicking &#8220;Next.&#8221;</li>\n</ol>\n</li>\n<li>Make Payment. On the Payment page, you can review your fee breakdown and payment options.\n<ol>\n<li>Enter Payment Information.</li>\n<li>Fill in your card information, then click &#8220;Next.&#8221;</li>\n<li>Confirm your payment information is correct and click &#8220;Submit Payment.&#8221;</li>\n</ol>\n</li>\n<li>The screen will display a confirmation when you have completed the application submission. You can visit the dashboard and click the &#8220;Applications&#8221; tab to check your status. You do NOT need to mail your application because it was submitted electronically. You will receive a submission confirmation and payment receipt via email.</li>\n<li>Once the application has been submitted, it will be reviewed by the OMMA.\n<ol>\n<li>If approved, an approval letter with your license will be mailed via US Mail within 14 business days of the application submission date.</li>\n<li>If an application is incomplete, you will receive an email stating the reason(s). Log back into your account to make corrections.</li>\n</ol>\n</li>\n<li>Purchase medical marijuana products from a licensed dispensary.\n<ol>\n<li>Present your medical marijuana identification card — or, since January 6, 2026, your OMMA approval email or approved MedPortal status — together with a government photo ID any time you purchase medical marijuana at a dispensary or have marijuana taken to a processor to be concentrated.</li>\n</ol>\n</li>\n</ol>\n<p><em>The OMMA recommends using Chrome, Edge or Firefox as your browser. Please DO NOT use Internet Explorer.</em></p>\n<p><strong>Please note:</strong> You have <strong>30 days</strong> from the date of your certification to complete the state application. Failure to do so may result in additional fees.</p>\n',
          section_id: 'state-renewal',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '5295ad08',
          container_size: 'default',
          content:
            '<h2>Legal limitations for cardholders</h2>\n<ul>\n<li>Oklahoma voters created the medical marijuana program through State Question 788 in June 2018; the Oklahoma Medical Marijuana and Patient Protection Act (House Bill 2612, 2019) set the framework now administered by the Oklahoma Medical Marijuana Authority (OMMA). Under 63 O.S. § 420, a licensed patient may possess up to 3 ounces of marijuana on their person, 8 ounces at their residence, 1 ounce of concentrate, 72 ounces of edibles and 72 ounces of topicals, and may grow 6 mature plants and 6 seedlings.</li>\n<li>Under <a href="https://oklahoma.gov/omma/patients-caregivers/patient-rights-and-responsibilities.html">Oklahoma medical marijuana law</a>, patients with a card have the right to:\n<ul>\n<li aria-level="1">Enroll in school without rejection based only on cardholder status.</li>\n<li aria-level="1">Rent an apartment without eviction or refusal based solely on patient status, unless the landlord would lose a federal monetary or licensing benefit.</li>\n<li aria-level="1">Not be disciplined, fired or refused a job solely for holding a card or testing positive for marijuana — unless the job is safety-sensitive, or marijuana is possessed or used at work. From November 1, 2026 (House Bill 3127), an employer with a written drug-testing policy that complies with Oklahoma’s Standards for Workplace Drug and Alcohol Testing Act may act on a positive marijuana test, and safety-sensitive roles are zero-tolerance. Check your employer’s policy.</li>\n<li aria-level="1">Receive healthcare without disqualification merely due to the medical use of marijuana.</li>\n<li aria-level="1">Visit or have custody of their child without denial based on patient status.</li>\n<li aria-level="1">Hold a state-issued professional license or a concealed-carry permit without suspension on the sole basis of cardholder status.</li>\n</ul>\n</li>\n</ul>\n<p>However, an Oklahoma medical marijuana patient license does not give you the right to:</p>\n<ul>\n<li style="list-style-type: none;">\n<ul>\n<li aria-level="1">Use or possess marijuana at your place of employment.</li>\n<li aria-level="1">Work, live, or learn somewhere that could face loss of a monetary or licensing benefit under federal law due to your patient status. Federal employees and commercial driver’s license (CDL) holders remain subject to federal drug rules regardless of state licensure.</li>\n<li aria-level="1">Sell or distribute marijuana to others without a commercial medical cannabis license.</li>\n<li aria-level="1">Buy a firearm free of federal restrictions. Oklahoma law protects a licensee’s right to own a firearm, but federal law (18 U.S.C. § 922(g)(3)) still bars firearm possession by an &#8220;unlawful user&#8221; of a controlled substance. In May 2026 ATF proposed revising Form 4473 so the question no longer covers state-authorized medical marijuana use; that revision had not been finalized as of September 2026. Speak to an attorney before buying a firearm.</li>\n</ul>\n</li>\n<li>Recreational cannabis remains illegal in Oklahoma — voters rejected State Question 820 in March 2023.</li>\n<li>Hemp-derived <a href="https://leafwell.com/blog/delta-8-thc">delta-8</a>, <a href="https://leafwell.com/blog/delta-10-thc">delta-10</a> and similar products made from <a href="https://leafwell.com/blog/what-is-cbd">CBD</a> are sold outside OMMA’s program and are not covered by your patient license. Under the federal 2026 Extensions Act (H.R. 5371, signed November 12, 2025), the federal definition of hemp narrows on November 12, 2026; after that date most intoxicating hemp-derived THC products will no longer be federally lawful.</li>\n<li>Telemedicine is allowed for medical marijuana certification in Oklahoma. This means that you can use Leafwell to get certified online.</li>\n</ul>\n',
          section_id: 'laws',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'f775c8cd',
          container_size: 'default',
          content:
            '<h2>How to become a caregiver in Oklahoma</h2>\n<p>Caregiver licenses are available to family members or assistants who regularly look after a medical marijuana patient for whom they are applying to be a caregiver. It allows the caregiver to buy, transport, possess, and administer medical marijuana or medical marijuana products to licensed medical cannabis patients.</p>\n<p>To apply, the patient’s recommending physician must certify on the Physician Recommendation Form that a caregiver is needed, and patient and caregiver both sign OMMA’s Caregiver Designation Form. Caregivers must be Oklahoma residents aged 18 or over; there is no application fee, the license is valid for up to two years, and a patient may have one licensed caregiver at a time.</p>\n<p>&nbsp;</p>\n',
          section_id: 'caregiver',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: 'e5ad9939',
          container_size: 'default',
          content:
            '<h2>Minor patient information</h2>\n<p>Patients under 18 apply with a parent or legal guardian, who signs the application and provides their own proof of identity and Oklahoma residency alongside the minor’s. Minors need recommendations from <strong>two physicians</strong> on OMMA’s Minor Patient Physician Recommendation Form. Minor licenses are valid for up to two years and carry the same state fee as adult licenses: $100 + $4.30 processing, or $20 + $2.50 at the reduced rate.</p>\n<p>The parent or guardian who signs the application is named on the minor’s license. If the recommending physician certifies that a caregiver is needed, a licensed caregiver can be designated to buy and administer medical marijuana on the minor’s behalf.</p>\n',
          section_id: 'minor',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'rich_text',
          instance_id: '89a3f45d',
          container_size: 'default',
          content:
            '<h2>Can I use my Oklahoma medical marijuana card in other states?</h2>\n<p>Oklahoma also welcomes visiting patients. If you hold a state-government-issued medical marijuana license from another state, you can apply to OMMA for a 30-day <a href="https://oklahoma.gov/omma/patients-caregivers/patient-licenses.html">out-of-state (temporary) patient license</a> — $100 plus a $4.30 processing fee, no Oklahoma physician visit required — to buy, use and grow medical marijuana in Oklahoma. You can apply for a new one from one week before it expires. Licenses issued by an out-of-state medical provider rather than a state government (for example, Texas) do not qualify.</p>\n<p>Going the other way, whether your Oklahoma card is useful depends on the state — and in many places it no longer matters, because cannabis is legal for adults 21 and over regardless of any card.</p>\n<p><strong>Where you do not need your card at all</strong></p>\n<p>Cannabis is legal for adults 21 and over in these states, so you can buy from a licensed adult-use store with nothing but a government photo ID. Some of them also recognize an Oklahoma medical card, which can mean a tax exemption or a higher limit.</p>\n<ul>\n<li aria-level="1"><strong>Arizona</strong> — adult use is legal. Your Oklahoma card protects possession but does <strong>not</strong> let you buy medical cannabis here (see below).</li>\n<li aria-level="1"><strong>Delaware</strong> — adult use is legal, with retail sales open since August 1, 2025. Dispensaries can also register you as a visiting medical patient on the spot via a QR-code application.</li>\n<li aria-level="1"><strong>Maine</strong> — adult use is legal. Maine also accepts Oklahoma cards directly: Oklahoma is on Maine’s approved-state list, and a visiting patient may buy up to 2.5 ounces every 15 days with no Maine application. Carry your physical OMMA card — Maine rejects digital and temporary credentials.</li>\n<li aria-level="1"><strong>Michigan</strong> — adult use is legal. Michigan dispensaries <em>may</em> also serve visiting medical patients, at the dispensary’s discretion.</li>\n<li aria-level="1"><strong>Nevada</strong> — adult use is legal, and Nevada recognizes your Oklahoma card. Worth carrying: medical patients are exempt from the 10% retail excise tax.</li>\n<li aria-level="1"><strong>New Jersey</strong> — adult use is legal. <strong>New Jersey does not offer reciprocity.</strong> To buy as a medical patient you would have to be certified by a New Jersey practitioner and enroll in New Jersey’s own registry.</li>\n<li aria-level="1"><strong>New Mexico</strong> — adult use is legal, and New Mexico recognizes your card as a &#8220;reciprocal participant.&#8221; Worth carrying: medical purchases are tax-free and the limit is higher.</li>\n<li aria-level="1"><strong>Rhode Island</strong> — adult use is legal. Rhode Island also recognizes out-of-state cards if you show a government ID issued by the same state as your card and complete an intake form.</li>\n<li aria-level="1"><strong>Washington, D.C.</strong> — you do not need an Oklahoma card. Anyone 21 or over can self-certify at a licensed D.C. dispensary; D.C. also names Oklahoma on its medical reciprocity list.</li>\n</ul>\n<p><strong>Medical-only states where your Oklahoma card matters</strong></p>\n<ul>\n<li aria-level="1"><strong>Arkansas</strong> — apply for a visiting patient registration: <strong>$50</strong>, valid <strong>90 days</strong>, allow about two weeks for processing. Apply before you travel.</li>\n<li aria-level="1"><strong>Hawaii</strong> — register as an out-of-state patient (Form 329): <strong>$49.50</strong>, valid <strong>60 days</strong>, renewable once per calendar year. Apply before you travel.</li>\n<li aria-level="1"><strong>Louisiana</strong> — your Oklahoma card is accepted directly. No state application; the pharmacy will have you complete its own attestation form at the counter.</li>\n<li aria-level="1"><strong>New Hampshire</strong> — your Oklahoma card is accepted directly and you may purchase. No application needed.</li>\n<li aria-level="1"><strong>Puerto Rico</strong> — your Oklahoma card is accepted directly. No application needed.</li>\n<li aria-level="1"><strong>Utah</strong> — Utah does not accept your Oklahoma card by itself. You must obtain a Utah non-resident card, valid <strong>21 days</strong> and limited to two per calendar year, and <strong>you must have one of Utah’s qualifying conditions</strong>. Oklahoma cards do not list a condition, so be ready to document yours.</li>\n</ul>\n<p><strong>States that protect possession but will not sell to you</strong></p>\n<ul>\n<li aria-level="1"><strong>Arizona</strong> — your card is a defense against arrest for possession, but Arizona law does not permit visiting patients to <strong>purchase</strong> medical cannabis. Dispensaries must query a verification system a visitor is not in.</li>\n<li aria-level="1"><strong>South Dakota</strong> — recognizes out-of-state cards for possession of up to three ounces; you cannot buy from a South Dakota dispensary.</li>\n</ul>\n<p>Wherever you go, carrying cannabis across a state line remains a federal offense even between two states where it is legal, and it is never permitted on a plane, on federal land or in a national park. Buy where you are and use it there.</p>\n<p><em>Reciprocity rules verified 23 September 2026 against each destination state’s program. Confirm with the destination before you travel.</em></p>\n',
          section_id: 'reciprocity',
        },
        {
          acf_fc_layout: 'line',
          container_size: 'default',
          color: 'gray',
        },
        {
          acf_fc_layout: 'faqs',
          instance_id: '02b2a45c',
          section_id: 'faq',
          title: 'Frequently asked questions',
          description: '',
          items: [
            {
              id: 99586,
              title: 'How do you get a medical marijuana card in Oklahoma?',
              content:
                "There are three steps. First, complete a telehealth evaluation with an Oklahoma-licensed physician — Leafwell's is $59 — who decides whether medical marijuana is appropriate for you, since Oklahoma has no set list of qualifying conditions. Second, apply to the Oklahoma Medical Marijuana Authority (OMMA) through the OMMA MedPortal and pay the state application fee. Third, OMMA reviews your application within 14 business days and issues a patient license valid for up to two years.",
            },
            {
              id: 99588,
              title: 'How much does a medical marijuana card cost in Oklahoma?',
              content:
                "A Leafwell telehealth evaluation costs $59, the same for new patients and renewals. Oklahoma charges a separate state application fee of $100 plus a $4.30 credit-card processing fee. A reduced state fee of $20 plus a $2.50 processing fee applies if you are enrolled in Medicaid (SoonerSelect) or Medicare, or are a 100% disabled veteran. Total out of pocket is $163.30 at the standard rate, or $81.50 at the reduced rate. OMMA's fees are non-refundable, including if your application is not approved.",
            },
            {
              id: 99590,
              title: 'What is a $25 medical card in Oklahoma?',
              content:
                "\"$25 medical card\" refers to a discounted physician-evaluation fee advertised by some Oklahoma telehealth clinics. It is not a state price and it is not the total cost. Oklahoma's application fee is charged separately by OMMA — $100 plus a $4.30 processing fee, or $20 plus $2.50 at the reduced rate — and it is the same whatever you pay for the evaluation. OMMA's fee is non-refundable even if your application is not approved. Leafwell's $59 evaluation fee is refunded if you are not approved.",
            },
            {
              id: 98711,
              title:
                'Do you need a qualifying condition to get a medical marijuana card in Oklahoma?',
              content:
                'No. Oklahoma does not have a set list of qualifying conditions. A state-licensed physician decides whether medical marijuana is appropriate for you based on their professional judgment during your evaluation, then you apply to the Oklahoma Medical Marijuana Authority (OMMA) for your card.',
            },
            {
              id: 99592,
              title:
                'What are the requirements for a medical marijuana card in Oklahoma?',
              content:
                "You must be an Oklahoma resident and provide proof of identity and proof of residency — an Oklahoma driver's license or state ID, a U.S. passport, an accepted tribal ID, a utility bill from the previous calendar month, a property deed, a rental agreement, or last year's Oklahoma tax return. You also need a physician recommendation form dated within the previous 30 days, a clear digital photo, and a card to pay the state fee. Applicants apply for themselves from age 18; a minor applies through a parent or legal guardian and needs recommendation forms signed by two registered physicians. Oklahoma does not require a specific qualifying condition.",
            },
            {
              id: 82351,
              title:
                'Can I apply for my medical marijuana card online in Oklahoma?',
              content:
                'Yes. Oklahoma permits telemedicine for medical marijuana evaluations, so the physician appointment can be completed online. The state application is filed online too, through the OMMA MedPortal at oklahoma.gov. No in-person visit is required at any stage.',
            },
            {
              id: 84233,
              title:
                'Can I get a medical marijuana card in Oklahoma the same day?',
              content:
                "Your Leafwell evaluation can usually be completed the same day. Your state license cannot: Oklahoma law gives OMMA 14 business days to review a patient license application, plus one to two more business days to print the card. Once OMMA approves you, though, you can shop that day — since OMMA's guidance of January 6, 2026, an approval email or approved status in the OMMA MedPortal, shown with a government photo ID, is accepted at licensed dispensaries.",
            },
            {
              id: 84239,
              title:
                'Can I go to a dispensary before my card arrives in Oklahoma?',
              content:
                'Yes, once OMMA has approved your application. Under OMMA guidance issued January 6, 2026, dispensaries may accept an official OMMA approval email, approved status in the OMMA MedPortal, or a photo of your physical card, presented together with a valid government-issued photo ID. The dispensary confirms your active status through OMMA Verify at the point of sale. You cannot buy before your application is approved.',
            },
            {
              id: 84243,
              title:
                'Is there a grace period for expired medical marijuana cards in Oklahoma?',
              content:
                'No. OMMA does not publish a grace period for expired patient licenses, and dispensaries must verify an active license at the point of every purchase. Once your license expires you cannot buy at a dispensary until a new one is approved. Because OMMA has up to 14 business days to process an application, start your renewal well before your expiry date.',
            },
            {
              id: 82355,
              title:
                'How often do I need to renew my Oklahoma medical marijuana card?',
              content:
                'An Oklahoma patient license is valid for up to two years, so you renew every two years. The renewal evaluation can be done online with Leafwell at the same $59 price, and the physician recommendation you submit must be dated within the previous 30 days. Because OMMA has up to 14 business days to process the application and there is no grace period once a license expires, start at least three weeks before your expiry date.',
            },
            {
              id: 84237,
              title:
                'What are the possession limits for medical marijuana in Oklahoma?',
              content:
                'An Oklahoma patient license holder may possess up to 3 ounces of marijuana on their person, 8 ounces at their residence, 1 ounce of concentrated marijuana, 72 ounces of edible marijuana and 72 ounces of topical marijuana. Licensed patients may also grow 6 mature plants and 6 seedlings. These are the limits set by Oklahoma law, not a recommended amount to use.',
            },
            {
              id: 84235,
              title:
                'Can I own a gun if I have a medical marijuana card in Oklahoma?',
              content:
                'Oklahoma law protects your right to own a firearm: 63 O.S. § 427.8 says a medical marijuana patient or caregiver licensee shall not be denied the right to own, purchase or possess a firearm, ammunition or firearm accessories, or a concealed-carry permit, based solely on their license. Federal law is the problem. 18 U.S.C. § 922(g)(3) bars firearm possession by an unlawful user of a controlled substance, and marijuana remains a controlled substance even after state-licensed medical marijuana moved from Schedule I to Schedule III on April 28, 2026. In May 2026 the ATF proposed revising Form 4473 so the marijuana question no longer covers state-authorized medical use, but that revision had not been finalized as of September 2026. Until it is, buying a firearm while holding a card carries federal legal risk, and Oklahoma law cannot protect you from federal charges. Speak to an attorney before you buy.',
            },
            {
              id: 84241,
              title:
                'Can I have a medical marijuana card and a CDL in Oklahoma?',
              content:
                'Not in practice. The U.S. Department of Transportation prohibits marijuana use by anyone in a safety-sensitive role, including commercial drivers, and tests for it. That did not change when state-licensed medical marijuana moved to Schedule III on April 28, 2026: DOT-regulated drivers are still tested for THC, a Medical Review Officer may not accept a medical marijuana card or recommendation as a valid explanation for a positive test, and a positive result removes you from driving duties and is reported to the FMCSA Drug and Alcohol Clearinghouse. Oklahoma&amp;#8217;s employment protections for cardholders do not cover positions governed by federal rules.',
            },
            {
              id: 82357,
              title: 'What are the medical marijuana laws in Oklahoma?',
              content:
                'Oklahoma voters legalized medical marijuana through State Question 788 in June 2018. The Oklahoma Medical Marijuana and Patient Protection Act — House Bill 2612, signed in 2019 and codified at Title 63 of the Oklahoma Statutes, Section 427.1 and following — created the framework now administered by the Oklahoma Medical Marijuana Authority. There is no list of qualifying conditions, patient licenses are valid for up to two years, and recreational marijuana remains illegal in Oklahoma.',
            },
            {
              id: 99881,
              title:
                'What disqualifies you from getting a medical card in Oklahoma?',
              content:
                'Not your diagnosis — Oklahoma has no list of qualifying conditions, so nobody is disqualified by condition. Applications fail for procedural reasons: you are not an Oklahoma resident (non-residents can only apply for a 30-day out-of-state license, and only if they already hold another state government&amp;#8217;s medical marijuana license); you are under 18 and apply without a parent or legal guardian and two physicians&amp;#8217; recommendations; your identity, residency or photo documents are missing or unreadable; or your physician recommendation is more than 30 days old when you submit. The physician can also decline to recommend you after the evaluation — Leafwell refunds its $59 fee if you are not approved. OMMA emails you if an application is incomplete so you can correct it, and mails a denial letter stating the reason within 14 business days if the requirements are not met.',
            },
          ],
          button: '',
        },
        {
          acf_fc_layout: 'table_of_contents',
          instance_id: '0ee348bb',
          table_of_contents: [
            {
              label: 'Benefits of Getting a Card',
              id: 'benefits-card',
            },
            {
              label: 'State-Specific Steps',
              id: 'state-specific-steps',
            },
            {
              label: 'Testimonials',
              id: 'testimonials',
            },
            {
              label: 'What You Need to Know',
              id: 'what-you-need-to-know',
            },
            {
              label: 'Qualifying Conditions',
              id: 'qualifying-conditions',
            },
            {
              label: 'What You Need',
              id: 'what-you-need',
            },
            {
              label: 'Cost Information',
              id: 'cost-information',
            },
            {
              label: 'More Questions',
              id: 'more-questions',
            },
            {
              label: 'Next steps after approval',
              id: 'state-steps',
            },
            {
              label: 'How to renew your OK medical marijuana card',
              id: 'renew',
            },
            {
              label: 'Next steps after renewal',
              id: 'state-renewal',
            },
            {
              label: 'Legal limitations for cardholders',
              id: 'laws',
            },
            {
              label: 'How to become a caregiver in Oklahoma',
              id: 'caregiver',
            },
            {
              label: 'Minor patient information',
              id: 'minor',
            },
            {
              label:
                'Can I use my Oklahoma medical cannabis card in other states?',
              id: 'reciprocity',
            },
            {
              label: 'FAQs',
              id: 'faqs',
            },
          ],
        },
      ],
    },
    seo: {
      title: 'Oklahoma Medical Marijuana Card Online, No Condition Needed',
      description:
        'No qualifying condition needed in Oklahoma. Get certified online: $59 evaluation + OMMA’s $104.30 state fee. OMMA approves in 14 business days.',
      canonical: 'https://leafwell.com/medical-card/oklahoma',
      images: [
        {
          source_url:
            'https://admin.leafwell.com/app/uploads/2025/05/medical-card-oklahoma.jpg',
          alt_text: 'person holding an Oklahoma medical card',
          media_details: {
            width: 637,
            height: 637,
          },
        },
      ],
      meta_robots_noarchive: false,
      meta_robots_nofollow: false,
      meta_robots_noindex: false,
      opengraph: {
        title: 'Oklahoma Medical Marijuana Card Online, No Condition Needed',
        description:
          'No qualifying condition needed in Oklahoma. Get certified online: $59 evaluation + OMMA’s $104.30 state fee. OMMA approves in 14 business days.',
        type: 'website',
      },
      redirect: null,
    },
    language: 'en',
    translations: [
      {
        uri: '/medical-card/oklahoma',
        slug: 'en',
        locale: 'en',
      },
      {
        uri: '/es/carta-recomendacion/oklahoma',
        slug: 'es',
        locale: 'es_ES',
      },
    ],
  },
];

const PageContent: React.FC<PageProps> = ({
  page: {
    title,
    seo,
    date,
    modified,
    slug,
    acf: {
      hero,
      name,
      affiliateLogo,
      affiliateWebsite,
      formUrl,
      pauseAppointments,
      redirectMedicalCard,
      redirectProductEndpoint,
      redirectCannabisCounseling,
      description,
      imageObjectCaption,
      blocks,
      pageType,
      stateProductImage,
      product,
      totalMedicalCardPrice,
      states,
      city,
      hasReferral,
      isClinic,
    },
  },
  settings: { seoSettings },
  variant,
  featureFlag,
  inPersonEvents,
}) => {
  const posthog = usePostHog();

  useEffect(() => {
    if (featureFlag) {
      posthog.capture('$feature_flag_called', {
        $feature_flag: featureFlag,
        $feature_flag_response: variant,
      });
      posthog.capture('$pageview', {
        experiment_page: true,
      });
    }
  }, [featureFlag, variant]);
  return (
    <PageWrap seo={seo} pageType={pageType} title={title}>
      <MedicalWebPageJson
        seo={seo}
        date={date}
        title={title}
        modified={modified}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
        siteUrl={seoSettings.siteUrl}
        name={name}
        description={description}
        imageObjectCaption={imageObjectCaption}
        siteDescription={seoSettings.homepage.homepageDescription}
      />
      <ServiceJson
        seo={seo}
        product={product}
        totalMedicalCardPrice={totalMedicalCardPrice}
        stateName={slug.replace(/-/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        })}
      />
      {variant === 'test' ? (
        <>
          <HeroCardNew
            hero={hero}
            states={states}
            affiliateLogo={affiliateLogo}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            inPersonEvents={inPersonEvents}
          />
          <StickyMobileCta
            observeTargetId="hero-cta-sentinel"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      ) : (
        <>
          <HeroCard
            hero={hero}
            pageType={pageType}
            states={states}
            formUrl={formUrl}
            affiliateLogo={affiliateLogo}
            affiliateWebsite={affiliateWebsite}
            pauseAppointments={pauseAppointments}
            redirectMedicalCard={redirectMedicalCard}
            redirectCannabisCounseling={redirectCannabisCounseling}
            totalMedicalCardPrice={totalMedicalCardPrice}
            city={city}
            isClinic={isClinic}
            redirectProductEndpoint={redirectProductEndpoint}
          />
          <StickyMobileCta
            observeTargetId="leafwell-signup-form"
            stateCode={states?.[0]}
            formSubmitText={hero.formSubmitText || 'Get your card'}
          />
        </>
      )}
      {blocks && blocks.length > 0 ? (
        <Blocks
          blocks={blocks}
          pageType={pageType}
          states={states}
          modified={modified}
          stateProductImage={stateProductImage}
          hasReferral={hasReferral}
          variant={variant}
        />
      ) : null}
      <MedicalReviewer modified={modified} />
    </PageWrap>
  );
};

const Page: React.FC<PageProps> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <PageContent {...props} />;
};

export const getServerSideProps = (async context => {
  const { req } = context;
  const cookies = parse(req.headers.cookie || '');

  let distinctId: string;
  try {
    const phCookie = cookies[POSTHOG_COOKIE_KEY];
    distinctId = phCookie
      ? JSON.parse(phCookie).distinct_id
      : crypto.randomUUID();
  } catch {
    distinctId = crypto.randomUUID();
  }

  const bootstrapData = await posthogAPI.getFeatureFlags(distinctId, {
    $pathname: '/medical-card/oklahoma',
  });
  const newStatePageDesign =
    bootstrapData?.featureFlags?.[
      'website-new-state-page-design-more-info-screen-OK'
    ] ?? 'control';

  return await getPageServerSide({
    ...context,
    variant: newStatePageDesign,
    featureFlag: 'website-new-state-page-design-more-info-screen-OK',
    slug: 'medical-card/oklahoma',
    staticPage: camelcaseKeys(staticPage[0], { deep: true }),
  });
}) satisfies GetServerSideProps<PageProps>;

export default Page;
